import { calendarEventsLockedStore } from '../store/lockedCalendarStore';
import { lazyDeleteCalendarEventWithConfirm } from 'owa-calendar-event-actions-with-confirm';
import { isSeriesInstance, isSeriesMaster } from 'owa-calendar-event-capabilities';
import { lazyOpenNotesEditor } from 'owa-calendar-notes-editor';
import type CalendarTime from 'owa-calendar-types/lib/types/CalendarTime';
import { CalendarComponent } from 'owa-calendar-usage-instrumentation';
import { ControlIcons } from 'owa-control-icons';
import loc from 'owa-localize';
import { forwardingInstanceInfoMessage } from 'owa-locstrings/lib/strings/forwardinginstanceinfomessage.locstring.json';
import { forwardingMasterInfoMessage } from 'owa-locstrings/lib/strings/forwardingmasterinfomessage.locstring.json';
import { forwardingSingleEventInfoMessage } from 'owa-locstrings/lib/strings/forwardingsingleeventinfomessage.locstring.json';
import { lazyPopoutCalendarCompose, lazyPopoutCalendarReadingPane } from 'owa-popout-calendar';
import popoutMailComposeReply from 'owa-popout/lib/utils/popout/popoutMailComposeReply';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import EventScope from 'owa-service/lib/contract/EventScope';
import messageType from 'owa-service/lib/factory/message';
import { orchestrator } from 'satcheljs';
import {
    cancelCalendarEvent,
    forwardCalendarEventService,
    respondToCalendarEvent,
} from 'owa-calendar-events-actions';
import {
    getInitializedEventId,
    getInitializedEvent,
    isEventInitialized,
    getSourceId,
} from '../selectors/calendarEventContextMenuStoreSelectors';
import {
    cancelMeeting,
    deleteEvent,
    fetchEventData,
    forwardEvent,
    proposeNewTime,
    replyToMeeting,
    respondToMeeting,
    updateInitializedEventId,
    verifyEventData,
    openEventDetails,
    openEventContextMenu,
    duplicateEvent,
} from '../actions/calendarEventContextMenuStoreActions';

export const openEventContextMenuOrchestrator = orchestrator(
    openEventContextMenu,
    actionMessage => {
        const { viewState } = actionMessage;
        if (!viewState || !viewState.itemId) {
            updateInitializedEventId(null);
        } else {
            fetchEventData(viewState.itemId);
        }
    }
);

export const fetchEventDataOrchestrator = orchestrator(fetchEventData, async actionMessage => {
    const { itemId } = actionMessage;

    if (!itemId || itemId === getInitializedEventId()) {
        return;
    }

    updateInitializedEventId(null);
    const { fullEventPromise } = calendarEventsLockedStore.fetchFullCalendarEvent(
        itemId,
        undefined /** folderId */,
        undefined /** getExistingCalendarEvent */,
        undefined /** eventScope */,
        { fetchSource: getSourceId() } /** customData */
    );
    const event = await fullEventPromise;
    if (event) {
        updateInitializedEventId(itemId);
    }
});

export const verifyEventDataOrchestrator = orchestrator(verifyEventData, actionMessage => {
    const { handleItemRemoved } = actionMessage;
    if (isEventInitialized() && !getInitializedEvent()) {
        handleItemRemoved?.();
    }
});

export const replyToMeetingOrchestrator = orchestrator(replyToMeeting, actionMessage => {
    const { isReplyAll } = actionMessage;

    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    const referenceItem = messageType({
        ItemId: calendarItem.ItemId,
        From: calendarItem.Organizer,
        ToRecipients: (calendarItem.RequiredAttendees || [])
            .map(attendee => attendee.Mailbox)
            .filter(x => x),
        CcRecipients: (calendarItem.OptionalAttendees || [])
            .map(attendee => attendee.Mailbox)
            .filter(x => x),
        Subject: calendarItem.Subject,
        ConversationId: calendarItem.ConversationId ? calendarItem.ConversationId : undefined,
    });

    popoutMailComposeReply(isReplyAll, referenceItem);
});

export const forwardEventOrchestrator = orchestrator(forwardEvent, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    actionMessage.eventScope == EventScope.AllInstancesInSeries
        ? lazyOpenNotesEditor.importAndExecute(
              calendarItem.SeriesMasterItemId,
              EventScope.Default,
              (
                  eventScope: EventScope,
                  notes: string,
                  proposedTime: any,
                  sendResponse: boolean,
                  forwardees: AttendeeType[]
              ) => {
                  forwardees.length > 0 &&
                      forwardCalendarEventService(
                          calendarItem.ItemId,
                          forwardees,
                          notes,
                          eventScope
                      );
              },
              loc(forwardingMasterInfoMessage),
              ControlIcons.ForwardEvent,
              undefined,
              [],
              false, // Show notify toggle
              true // Send response
          )
        : lazyOpenNotesEditor.importAndExecute(
              calendarItem.ItemId,
              EventScope.Default,
              (
                  eventScope: EventScope,
                  notes: string,
                  proposedTime: any,
                  sendResponse: boolean,
                  forwardees: AttendeeType[]
              ) => {
                  forwardees.length > 0 &&
                      forwardCalendarEventService(
                          calendarItem.ItemId,
                          forwardees,
                          notes,
                          eventScope
                      );
              },
              isSeriesInstance(calendarItem)
                  ? loc(forwardingInstanceInfoMessage)
                  : loc(forwardingSingleEventInfoMessage),
              ControlIcons.ForwardEvent,
              undefined,
              [],
              false, // Show notify toggle
              true // Send response
          );
    openEventDetails(
        actionMessage.eventScope == EventScope.AllInstancesInSeries
            ? calendarItem.SeriesMasterItemId
            : calendarItem.ItemId
    );
});

export const respondToMeetingOrchestrator = orchestrator(respondToMeeting, actionMessage => {
    const { newResponseType, eventScope, editorTitleText } = actionMessage;

    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    const sourceId = getSourceId();

    if (calendarItem.IsResponseRequested) {
        const onSend = (
            eventScope: EventScope,
            notes: string,
            proposeNewTime: CalendarTime,
            sendResponse: boolean
        ) => {
            respondToCalendarEvent(
                calendarItem.ItemId,
                calendarItem.ResponseType,
                newResponseType,
                calendarItem.FreeBusyType,
                eventScope,
                sendResponse,
                sourceId,
                notes,
                proposeNewTime,
                false /* Should show declined events */
            );
        };
        lazyOpenNotesEditor.importAndExecute(
            calendarItem.ItemId,
            eventScope,
            onSend,
            editorTitleText,
            ControlIcons.CalendarReply,
            undefined /**Propose new time */,
            null /**Forwardees */,
            true /** Show notify toggle */,
            true /** Send response */,
            false /** Is cancelled event */,
            false /** Is declined series instance */,
            false /** IsTxpShareEvent */,
            newResponseType
        );
        openEventDetails(calendarItem.ItemId);
    } else {
        respondToCalendarEvent(
            calendarItem.ItemId,
            calendarItem.ResponseType,
            newResponseType,
            calendarItem.FreeBusyType,
            eventScope,
            false /* Send Response */,
            sourceId
        );
    }
});

export const proposeNewTimeOrchestrator = orchestrator(proposeNewTime, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    lazyPopoutCalendarReadingPane.importAndExecute(calendarItem.ItemId, getSourceId(), {
        responseType: actionMessage.responseType,
    });
});

export const deleteEventOrchestrator = orchestrator(deleteEvent, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    lazyDeleteCalendarEventWithConfirm.importAndExecute(
        calendarItem,
        actionMessage.eventScope,
        isSeriesMaster(calendarItem),
        getSourceId(),
        false /*isInPopOutWindow*/
    );
});

export const cancelMeetingOrchestrator = orchestrator(cancelMeeting, actionMessage => {
    const { editorTitleText, eventScope } = actionMessage;
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    lazyOpenNotesEditor.importAndExecute(
        calendarItem.ItemId,
        eventScope,
        (eventScope: EventScope, notes?: string) => {
            cancelCalendarEvent(
                calendarItem.ItemId,
                getSourceId(),
                eventScope,
                CalendarComponent.CalendarEventContextMenu,
                notes
            );
        },
        editorTitleText,
        ControlIcons.RemoveEvent,
        undefined, // Propose new time
        null, // Forwardees
        false, // Show notify toggle
        true, // Send response
        true, // Is cancelled event
        false, // Is declined series instance
        false, // Is TxpShare event,
        'Cancel'
    );
    openEventDetails(calendarItem.ItemId);
});

export const duplicateEventOrchestrator = orchestrator(duplicateEvent, actionMessage => {
    const calendarItem = getInitializedEvent();

    if (!calendarItem) {
        return;
    }

    const entrySource = getSourceId() + 'Duplicate';

    lazyPopoutCalendarCompose.importAndExecute(
        { ItemId: calendarItem.ItemId },
        entrySource,
        false /* eventHasUpdates */,
        true /* shouldDuplicate */
    );
});
